// src/components/financials/VendorManagement.js
// Updated: 02/01/2026 - Fixed Hook Dependencies

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Button, TextField, Paper, Table, TableBody, TableCell, 
  TableHead, TableRow, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogActions, MenuItem, Typography, Chip 
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const VendorManagement = () => {
  const { userProfile } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentVendor, setCurrentVendor] = useState(null);
  const [formData, setFormData] = useState({ name: '', defaultCategory: '', contactInfo: '' });

  const churchId = userProfile?.churchId;

  // Wrapped in useCallback
  const fetchVendors = useCallback(async () => {
    if (!churchId) return;
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'vendors'));
    setVendors(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, [churchId]);

  const fetchCategories = useCallback(async () => {
    if (!churchId) return;
    try {
        const q = query(collection(db, 'churches', churchId, 'financials', 'data', 'expenseCategories'), orderBy('name'));
        const snap = await getDocs(q);
        setCategories(snap.docs.map(d => d.data().name));
    } catch (e) { console.error(e); }
  }, [churchId]);

  useEffect(() => {
    if (churchId) {
        fetchVendors();
        fetchCategories();
    }
  }, [churchId, fetchVendors, fetchCategories]); // Added dependencies

  const handleOpen = (vendor = null) => {
    if (vendor) {
      setFormData({ name: vendor.name, defaultCategory: vendor.defaultCategory || '', contactInfo: vendor.contactInfo || '' });
      setCurrentVendor(vendor);
    } else {
      setFormData({ name: '', defaultCategory: '', contactInfo: '' });
      setCurrentVendor(null);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!churchId) return;
    const ref = collection(db, 'churches', churchId, 'financials', 'data', 'vendors');
    try {
      if (currentVendor) {
        await updateDoc(doc(ref, currentVendor.id), { ...formData, updatedAt: serverTimestamp() });
      } else {
        await addDoc(ref, { ...formData, churchId, createdAt: serverTimestamp() });
      }
      setOpen(false);
      fetchVendors();
    } catch (e) { alert("Error saving vendor: " + e.message); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure? This will remove the vendor from the dropdown list.")) {
      await deleteDoc(doc(db, 'churches', churchId, 'financials', 'data', 'vendors', id));
      fetchVendors();
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Vendor & Payee Directory</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Add Payee</Button>
      </Box>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Payee Name</TableCell>
              <TableCell>Default Category</TableCell>
              <TableCell>Contact Info</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendors.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">No vendors found. Add one to get started.</TableCell></TableRow>
            ) : (
              vendors.map((v) => (
                <TableRow key={v.id}>
                  <TableCell sx={{ fontWeight: 'bold' }}>{v.name}</TableCell>
                  <TableCell>{v.defaultCategory && <Chip label={v.defaultCategory} size="small" />}</TableCell>
                  <TableCell>{v.contactInfo || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(v)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(v.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{currentVendor ? 'Edit Payee' : 'New Payee'}</DialogTitle>
        <DialogContent sx={{ pt: 2, minWidth: 400 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField 
              label="Payee Name" fullWidth required 
              value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} 
            />
            <TextField 
              select label="Default Category (Optional)" fullWidth 
              value={formData.defaultCategory} onChange={(e) => setFormData({...formData, defaultCategory: e.target.value})} 
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {categories.map((c, index) => <MenuItem key={index} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField 
              label="Contact / Account #" fullWidth 
              value={formData.contactInfo} onChange={(e) => setFormData({...formData, contactInfo: e.target.value})} 
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save Payee</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorManagement;